"use client";

import { PreviewCard } from "@base-ui/react";
import type { ArticleAuthor } from "@mw/shared/payload-types";
import { BaseLink, Link } from "@ui/components/core/Link";
import { pxToRem } from "@ui/lib/utils";
import { getImageProps } from "@utilities/payload";
import { clsx } from "clsx";
import Image from "next/image";
import type React from "react";
import { useEffect } from "react";

const authorPreviewCard = PreviewCard.createHandle<React.ReactElement>();

export function LinkedArticleAuthors({
  title,
  profileCTALabel,
  authors,
}: {
  title: string;
  profileCTALabel: string;
  authors: ArticleAuthor[];
}) {
  useEffect(() => {
    return () => {
      authorPreviewCard.close();
    };
  }, []);

  return (
    <div
      className='tw-relative tw-flex tw-flex-wrap tw-gap-y-3 tw-w-full tw-flex-none sm:tw-shrink sm:tw-w-auto sm:tw-max-w-[calc(50%-1.25rem)] md:tw-max-w-[50%]'
      style={{ paddingTop: pxToRem(22) }}
    >
      <dt className='tw-type-xs tw-text-gray-500 tw-absolute tw-top-0 tw-left-0'>{title}</dt>
      {authors.map((author, authorIndex) => {
        const imageProps = getImageProps(author.avatarImage, { fill: true, placeholder: true });

        return (
          <dd key={author.id} className='tw-type-base tw-text-black'>
            <span
              className={clsx(
                "tw-whitespace-pre-wrap",
                authorIndex !== authors.length - 1 && 'after:tw-content-[",_"]',
              )}
            >
              {author.slug ? (
                <PreviewCard.Trigger
                  handle={authorPreviewCard}
                  render={
                    <BaseLink
                      href={`/blog/authors/${author.slug}`}
                      className='hover:tw-underline hover:tw-decoration-1 hover:tw-underline-offset-2 tw-outline-none focus-visible:tw-outline-focus tw-rounded-sm'
                    >
                      {author.firstName} {author.lastName}
                    </BaseLink>
                  }
                  payload={
                    <div className='tw-w-64 tw-max-w-full tw-p-4'>
                      <div className='tw-flex tw-gap-2.5'>
                        <div className='tw-relative tw-size-11 tw-rounded-full tw-overflow-hidden tw-flex-none tw-bg-cream tw-flex tw-items-center tw-justify-center'>
                          {imageProps ? (
                            <Image {...imageProps} objectFit='cover' sizes={pxToRem(44)} />
                          ) : (
                            <svg viewBox='0 0 24 24' aria-hidden className='tw-size-6 tw-text-gray-500'>
                              <path
                                fill='currentColor'
                                d='M12 2a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9M12.001 12.5c-3.721 0-6.564 2.226-7.72 5.353a2.21 2.21 0 0 0 .39 2.225c.479.57 1.222.922 2.027.922h10.607c.805 0 1.548-.352 2.026-.922a2.21 2.21 0 0 0 .391-2.225c-1.156-3.127-3.999-5.353-7.72-5.353'
                              />
                            </svg>
                          )}
                        </div>
                        <div className='tw-my-auto'>
                          <p className='tw-type-base-tight tw-text-black'>
                            {author.firstName} {author.lastName}
                          </p>
                          {author.jobTitle && (
                            <p className='tw-mt-2.5 tw-type-xs-tight tw-text-gray-500'>{author.jobTitle}</p>
                          )}
                        </div>
                      </div>
                      <Link
                        asButton
                        buttonVariants={{ size: "md", variant: "elevated", expand: true }}
                        href={`/blog/authors/${author.slug}`}
                        className='tw-mt-6'
                      >
                        {profileCTALabel}
                      </Link>
                    </div>
                  }
                >
                  {author.firstName} {author.lastName}
                </PreviewCard.Trigger>
              ) : (
                <>
                  {author.firstName} {author.lastName}
                </>
              )}
            </span>
          </dd>
        );
      })}
      <PreviewCard.Root handle={authorPreviewCard}>
        {({ payload }) => (
          <PreviewCard.Portal>
            <PreviewCard.Positioner
              className='tw-duration-300 tw-h-[var(--positioner-height)] tw-w-[var(--positioner-width)] tw-max-w-[var(--available-width)] tw-ease-in-out'
              style={({ instant }) => ({
                transitionProperty: instant ? "none" : "top, left, right, bottom, transform",
              })}
            >
              <PreviewCard.Popup
                className={({ transitionStatus }) =>
                  clsx(
                    "tw-group tw-relative tw-h-[var(--popup-height,auto)] tw-w-[var(--popup-width,auto)] tw-p-2",
                    "tw-transition-[width,height,opacity,transform,mask-position] tw-duration-[0.3s,0.3s,0.2s,0.2s,0.2s] tw-ease-in-out tw-origin-[var(--transform-origin)]",
                    transitionStatus === "starting" && "-tw-translate-y-1",
                  )
                }
                style={({ transitionStatus }) => ({
                  maskImage: "linear-gradient(to bottom, white 33.333333%, transparent 66.666666%)",
                  maskSize: "100% 300%",
                  maskPosition: transitionStatus === "starting" || transitionStatus === "ending" ? "0% 100%" : "0% 0%",
                })}
              >
                <div
                  className='tw-bg-white tw-rounded-3xl'
                  style={{
                    boxShadow: "0 0 1px rgb(0 0 0 / 0.4), 0 1px 1px rgb(0 0 0 / 0.04), 0 2px 4px rgb(0 0 0 / 0.04)",
                  }}
                >
                  <PreviewCard.Viewport
                    className={clsx(
                      "tw-relative tw-h-full tw-w-full tw-overflow-clip",
                      "*:tw-transition-[transform,opacity] *:tw-ease-in-out *:tw-duration-300 *:tw-w-[var(--popup-width)]",
                      "data-[starting-style]:*:tw-opacity-0 data-[ending-style]:*:tw-opacity-0",
                      "data-[starting-style]:*:data-[activation-direction~='left']:-tw-translate-x-[30%]",
                      "data-[starting-style]:*:data-[activation-direction~='right']:tw-translate-x-[30%]",
                      "data-[ending-style]:*:data-[activation-direction~='right']:-tw-translate-x-[30%]",
                      "data-[ending-style]:*:data-[activation-direction~='left']:tw-translate-x-[30%]",
                    )}
                  >
                    {payload}
                  </PreviewCard.Viewport>
                </div>
              </PreviewCard.Popup>
            </PreviewCard.Positioner>
          </PreviewCard.Portal>
        )}
      </PreviewCard.Root>
    </div>
  );
}

export function PlainTextArticleAuthors({ authors, title }: { authors: ArticleAuthor[]; title: string }) {
  return (
    <div
      className='tw-relative tw-flex tw-flex-wrap tw-gap-y-3 tw-w-full tw-flex-none sm:tw-shrink sm:tw-w-auto sm:tw-max-w-[calc(50%-1.25rem)] md:tw-max-w-[50%]'
      style={{ paddingTop: pxToRem(22) }}
    >
      <dt className='tw-type-xs tw-text-gray-500 tw-absolute tw-top-0 tw-left-0'>{title}</dt>
      {authors.map((author, authorIndex) => (
        <dd key={author.id} className='tw-type-base tw-text-black'>
          <span
            className={clsx("tw-whitespace-pre-wrap", authorIndex !== authors.length - 1 && 'after:tw-content-[",_"]')}
          >
            {author.firstName} {author.lastName}
          </span>
        </dd>
      ))}
    </div>
  );
}
